import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import Modal from "../../../../../parts/modal-new";
import SvgIcon from "../../../../../parts/SvgIcon";
import scriptLoader from "../../../../../utils/scriptLoader";
import { simpleHash } from "../../../../../utils/string-helpers";

export const CustomMealModal = ({ locals }) => {
  const { t } = useTranslation("coaching-portal");
  const { mealPlan, query } = locals;
  const { athlete, mealPlan: mealPlanId } = query;
  const planMode = mealPlan?.planMode || "detailed";
  const basicMealTypes = ["Breakfast", "Lunch", "Snack", "Dinner"];
  
  // Get existing meals based on planMode
  let existingMeals = [];
  if (planMode === "simplified") {
    // For simplified, get meals from the first option of training or rest
    const { trainingDayOptions = [], restDayOptions = [] } = mealPlan;
    const firstOption = trainingDayOptions[0] || restDayOptions[0];
    existingMeals = firstOption?.meals || [];
    
    // If no meals exist, use default meals
    if (existingMeals.length === 0) {
      existingMeals = basicMealTypes.map((type, index) => ({
        type,
        order: index + 1,
        foods: [],
      }));
    }
  } else {
    // For detailed, get meals from weeks structure
    const { weeks = [] } = mealPlan;
    existingMeals = weeks[0]?.days[0]?.meals || [];
  }
  
  const orderedMeals = existingMeals.slice().sort((a, b) => a.order - b.order);
  const initialMealsWithOrder = orderedMeals.map(({ type, order }) => ({
    type,
    order,
    isEditing: false,
    originalType: type,
  }));
  const [state, setState] = useState({
    name: "",
    meals: initialMealsWithOrder,
    isReordered: false,
  });
  const { name, meals, isReordered } = state;
  const editInputRef = useRef(null);
  const isAnyEditing = meals.some((meal) => meal.isEditing);

  /**
   * Reorder
   */
  useEffect(() => {
    if (document.querySelector(".principal-grabber-div")) {
      scriptLoader(
        "https://cdn.jsdelivr.net/npm/sortablejs@1.15.7/Sortable.min.js",
        () => {
          const dragImage = document.createElement("img");
          dragImage.src =
            "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
          const sortable = new Sortable(
            document.querySelector(".principal-grabber-div"),
            {
              animation: 350,
              handle: ".drag-wrapper",
              setData(dataTransfer) {
                dataTransfer.setDragImage(dragImage, 0, 0);
              },
              onEnd: (e) => {
                if (e.oldIndex === e.newIndex) return;

                const items = e.from.querySelectorAll(":scope > div");
                e.from.insertBefore(
                  e.item,
                  items[e.oldIndex + (e.oldIndex > e.newIndex ? 0 : 1)]
                );

                setState((prevState) => {
                  const ordered = Array.from(items).map((element, index) => {
                    const mealName =
                      element.querySelector(".span-type")?.textContent || "";
                    const meal = prevState.meals.find(
                      (m) => m.type === mealName
                    );
                    return {
                      type: mealName,
                      order: index + 1,
                      originalType: meal?.originalType || mealName,
                      isEditing: meal?.isEditing || false,
                    };
                  });
                  return {
                    ...prevState,
                    meals: ordered,
                    isReordered: true,
                  };
                });

                return false;
              },
            }
          );
        }
      );
    }
  }, []);

  /**
   * handle the inputs value
   * @param {*} e
   */
  const handleInputChange = (e) => {
    let { value } = e.target;

    // removing special characters but allowing spaces
    value = value.replace(/[^\p{L}\p{N} ]/gu, "");
    setState({ ...state, name: value });
  };

  /**
   * Add custom meal
   */
  const handleAddCustomMeal = () => {
    if (meals.some(({ type }) => type.toLowerCase() === name.toLowerCase())) {
      const body = {
        type: "error",
        message: t("editMealPlan.customMealModal.mealExistsError"),
      };
      return submit(`/actions/common/toast`, {
        method: "POST",
        body,
      });
    }

    const newMeal = { type: name.trim(), order: meals.length + 1, foods: [] };

    setState((prevState) => {
      const updatedMeals = [...prevState.meals, newMeal];
      return {
        ...prevState,
        name: "",
        meals: updatedMeals,
      };
    });
  };

  /**
   * Save the custom meals
   * @returns
   */
  const handleSave = () => {
    const newMeals = meals.filter(
      (meal) => !existingMeals.some(({ type }) => type === meal?.type)
    );
    const hasNewMeals = newMeals.length > 0;
    //close modal if there isn't any change
    if (!hasNewMeals && !isReordered) {
      return;
    }

    const handle = hasNewMeals
      ? isReordered
        ? "add-reorder"
        : "add"
      : "reorder";

    const payload = {
      newMeals,
      bodyAction: "handle-custom-meal",
      handle,
      ...(isReordered && { newOrder: meals }),
    };

    const requestId = simpleHash(JSON.stringify(payload));

    const body = {
      ...payload,
      requestId,
      planMode,
    };

    submit(
      `/actions/meal-planner/update-days-meal-plan?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`,
      {
        method: "POST",
        body,
      }
    );
  };

  /**
   * Function to delete a custom meal
   * @param {*} e
   */
  const handleDeleteCustomMeal = (e, meal) => {
    const mealExists = existingMeals.some((m) => m.type === meal);
    //if the custom meal was recently added, we only have to update the state
    if (!mealExists) {
      setState((prevState) => {
        const updatedMeals = prevState.meals.filter((m) => m.type !== meal);
        return {
          ...prevState,
          meals: updatedMeals,
        };
      });
    } else {
      e.preventDefault();
      e.stopPropagation();
      if (
        process.browser &&
        confirm(t("editMealPlan.customMealModal.deleteConfirm"))
      ) {
        const body = {
          meal,
          bodyAction: "handle-custom-meal",
          handle: "delete",
          planMode,
        };

        submit(
          `/actions/meal-planner/update-days-meal-plan?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`,
          {
            method: "POST",
            body,
          }
        );
      }
    }
  };

  /**
   * Function to detect if the meal name is editable or not
   * @param {*} index
   * @param {*} value
   */
  const handleEditMealState = (index, value) => {
    setState((prevState) => {
      const updatedMeals = prevState.meals.map((meal, i) => ({
        ...meal,
        isEditing: i === index ? value : false,
      }));
      return { ...prevState, meals: updatedMeals };
    });

    if (value) {
      setTimeout(() => {
        editInputRef.current?.focus();
      }, 0);
    }
  };

  /**
   * Confirm the edition of a meal name * @param {*} index
   */
  const handleConfirmNameEdit = (index) => {
    const meal = meals[index];

    const isRenamed = meal.type !== meal.originalType;

    //validate duplicated name
    const nameConflict = meals.some(
      (m, i) => i !== index && m.type.toLowerCase() === meal.type.toLowerCase()
    );
    if (nameConflict) {
      submit(`/actions/common/toast`, {
        method: "POST",
        body: {
          type: "error",
          message: t("editMealPlan.customMealModal.mealExistsError"),
        },
      });

      //Revert the original name and finish the action with the return
      setState((prevState) => {
        const updatedMeals = prevState.meals.map((m, i) =>
          i === index ? { ...m, isEditing: true } : m
        );
        return { ...prevState, meals: updatedMeals };
      });
      return;
    }

    const newMealsList = meals.filter(
      (m) => !existingMeals.some((em) => em.type === m.originalType)
    );

    let handle = null;
    let payload = null;

    // --- 1) New meals + reorder ---
    if (newMealsList.length > 0 && isReordered) {
      handle = "add-reorder";
      payload = {
        bodyAction: "handle-custom-meal",
        handle,
        newMeals: newMealsList.map((m, i) => ({
          type: m.type,
          order: m.order || i,
          foods: [],
        })),
        newOrder: meals.map((m, i) => ({
          type: m.type,
          order: m.order || i,
        })),
        ...(isRenamed && { oldName: meal.originalType, newName: meal.type }),
      };
    }
    // --- 2) New meals (without reorder) ---
    else if (newMealsList.length > 0 && !isReordered) {
      handle = "add";
      payload = {
        bodyAction: "handle-custom-meal",
        handle,
        newMeals: newMealsList.map((m, i) => ({
          type: m.type,
          order: m.order || i,
          foods: [],
        })),
      };
    }
    // --- 3) Rename + reorder ---
    else if (isRenamed && isReordered) {
      handle = "edit-reorder";
      payload = {
        bodyAction: "handle-custom-meal",
        handle,
        oldName: meal.originalType,
        newName: meal.type,
        newOrder: meals.map((m, i) => ({
          type: m.type,
          order: m.order || i,
        })),
      };
    }
    // --- 4) just rename ---
    else if (isRenamed) {
      handle = "rename";
      payload = {
        bodyAction: "handle-custom-meal",
        handle,
        oldName: meal.originalType,
        newName: meal.type,
      };
    }

    if (payload) {
      submit(
        `/actions/meal-planner/update-days-meal-plan?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`,
        {
          method: "POST",
          body: {
            ...payload,
            planMode,
          },
        }
      );
    }

    //Update local state
    setState((prevState) => {
      const updatedMeals = prevState.meals.map((m, i) =>
        i === index ? { ...m, isEditing: false, originalType: m.type } : m
      );
      return { ...prevState, meals: updatedMeals };
    });
  };

  /**
   * Edit meal's name
   * @param {*} index
   * @param {*} value
   */
  const handleChangeMealName = (index, value) => {
    value = value.replace(/[^\p{L}\p{N} ]/gu, "");
    setState((prevState) => {
      const updatedMeals = prevState.meals.map((meal, i) =>
        i === index ? { ...meal, type: value } : meal
      );
      return { ...prevState, meals: updatedMeals };
    });
  };

  return (
    <Modal className="custom-meal-modal" title={t("editMealPlan.customMealModal.title")}>
      <div className="body">
        <div>
          <span className="opacity-70 text-[0.75rem]">{t("editMealPlan.customMealModal.meals")}</span>
        </div>
        <div className="order principal-grabber-div">
          {meals.map(({ type, isEditing, originalType }, index) => {
            const isBasicMeal = basicMealTypes.includes(type);
            const hasChanged = type.trim() !== "" && type !== originalType;
            return (
              <div key={`${type}-${index}`} className="row">
                {isEditing ? (
                  <input
                    ref={editInputRef}
                    className="name min-w-70.5!"
                    type="text"
                    value={type}
                    autoFocus
                    onChange={(e) =>
                      handleChangeMealName(index, e.target.value)
                    }
                  />
                ) : (
                  <div
                    className={isBasicMeal ? "name" : "name min-w-70.5!"}
                  >
                    <span className="span-type">{type}</span>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  {!isBasicMeal &&
                    (isEditing ? (
                      <button
                        onClick={() => {
                          if (hasChanged) {
                            handleConfirmNameEdit(index);
                          } else {
                            // Cancel edit
                            setState((prevState) => {
                              const updatedMeals = prevState.meals.map((m, i) =>
                                i === index
                                  ? {
                                      ...m,
                                      type: m.originalType,
                                      isEditing: false,
                                    }
                                  : m
                              );
                              return { ...prevState, meals: updatedMeals };
                            });
                          }
                        }}
                        data-button-type="primary"
                      >
                        <SvgIcon src="/img/svg/check.svg" classNames="check" />
                      </button>
                    ) : (
                      <button onClick={() => handleEditMealState(index, true)}>
                        <SvgIcon src="/img/new-icons/edit-pencil.svg" />
                      </button>
                    ))}
                  <div
                    className={
                      isAnyEditing ? " disabled drag-wrapper" : "drag-wrapper"
                    }
                  >
                    <SvgIcon src="/img/new-icons/menu.svg" />
                  </div>
                  {!isBasicMeal && (
                    <a
                      className={isAnyEditing ? "disabled" : ""}
                      onClick={(e) => {
                        handleDeleteCustomMeal(e, type);
                      }}
                    >
                      <SvgIcon
                        src="/img/new-icons/red-trash.svg"
                        noFilterOnLight={true}
                      />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="inputs">
          <div>
            <span className="opacity-70 text-[0.75rem]">New Meal</span>
          </div>

          <div className="flex items-center gap-2">
            <input
              disabled={isAnyEditing}
              type="text"
              placeholder={`Enter Meal Name`}
              value={name}
              onChange={handleInputChange}
            />
            <button
              data-button-type={"outline"}
              onClick={handleAddCustomMeal}
              disabled={name.length === 0}
            >
              <span>{t("editMealPlan.customMealModal.add")}</span>
            </button>
          </div>
        </div>
        <div className="footer">
          <button
            data-button-type="primary"
            onClick={handleSave}
            disabled={isAnyEditing}
          >
            {t("editMealPlan.customMealModal.save")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

CustomMealModal.propTypes = {
  locals: PropTypes.object,
};
